import {environment} from "src/environments/environment";
import {LocalizeRouterService} from "@gilsdav/ngx-translate-router";
import {Injectable} from "@angular/core";

/**
 * Utility for managing API URLs based on the environment and current language.
 * It simplifies the construction of URLs for different resources needed in the application.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiUrls {

  constructor(
      private localizeRouterService: LocalizeRouterService,
  ) {
  }

  private makeUrl(resource: string): string {
    return this.HOST + "/" + this.localizeRouterService.parser.currentLang + "/" + resource + "/";
  }

  private  readonly HOST = environment.API_BASE_URL;


  // User
  public readonly LOGIN = this.makeUrl("user/v2/login");
  public readonly SIGNUP = this.makeUrl("user/v2/new");
  public readonly RESET_PASSWORD = this.makeUrl("user/v2/reset");
  public readonly CONTACT = this.makeUrl("user/v3/contact");
  public readonly UPDATE_USER = this.makeUrl('user/update')
  public readonly DELETE_USER = this.makeUrl('user/useremail/deleteuser')
  public readonly SIGNUP_EMPLOYEE = this.makeUrl('user/employee/new')
  public readonly GET_USER = this.makeUrl('user/v2/user-detail')

  // Delivery points
  public readonly DELIVERY_POINTS = this.makeUrl('delivery_points');
  public readonly ORDER_TYPES = this.makeUrl('delivery_points/order_types')

  // Catalog search
  public readonly SEARCH_AUTOCOMPLETE = this.makeUrl("catalog/search");

  // Catalog
  public readonly CATALOG = this.makeUrl('catalog')
  public readonly RELATED_PRODUCTS = this.makeUrl('catalog/relatedproducts');
  public readonly SUGGESTED_PRODUCTS = this.makeUrl('catalog/suggestedList');
  public readonly USUAL_PURCHASE = this.makeUrl('catalog/usualpurchase');
  public readonly LIST_PRODUCTS = this.makeUrl('catalog/v2/list');
  public readonly FILTERS = this.makeUrl('catalog/filters');

  // Categories
  public readonly MAIN_CATEGORIES = this.makeUrl('data/main_categories')
  public readonly CATEGORIES = this.makeUrl('data/categories');

  // Products
  public readonly PRODUCT_DETAILS = this.makeUrl('catalog/fullproduct');
  public readonly PDF_CATALOG = this.makeUrl('catalog/report');
  public readonly PRODUCT_BY_EAN = this.makeUrl('catalog/ean');
  public readonly SHARE_PRODUCT_LINK = this.makeUrl('share-products-links');

  // Promotions
  public readonly PROMOTION_LIST = this.makeUrl('promotions/list');
  public readonly PROMOTION_DETAIL = this.makeUrl('promotions/detail');

  // Lists
  public readonly LISTS = this.makeUrl('lists');
  public readonly LISTS_V2 = this.makeUrl('lists/v2');
  public readonly LIST_DETAIL = this.makeUrl('lists/listdetail');

  // Orders
  public readonly ORDER_LIST = this.makeUrl('orders/list');
  public readonly ORDER_DETAIL = this.makeUrl('orders/detail');
  public readonly ORDER_RECIPT = this.makeUrl('orders/recipt');
  public readonly LAST_ORDER_ID = this.makeUrl('orders/lastOrderId');

  // Checkout
  public readonly CONFIRM_ORDER = this.makeUrl('orders');
  public readonly ADYEN_PAYMENT_METHODS = this.makeUrl('orders/v2/paymentMethods');

  // Coupon
  public readonly VALIDATE_COUPON = this.makeUrl('orders/v2/coupon');
  public readonly AVAILABLE_COUPONS = this.makeUrl('coupons/available');
  public readonly USED_COUPONS = this.makeUrl('coupons/used');
  public readonly ACCUMULATED_AMOUNT = this.makeUrl('coupons/acumulatedimport');

  // Banner
  public readonly BANNER = this.makeUrl('promotions/homebanner');

  // Master data
  public readonly WORKPLACES = this.makeUrl('data/workstation');
  public readonly COUNTRIES = this.makeUrl('data/countries');
  public readonly PROVINCES = this.makeUrl('data/provinces');

  public readonly PAY_ORDER = (deliveryPoint: number, orderCode: number) =>
      this.makeUrl(`orders/${deliveryPoint}/${orderCode}/payment`);

  public readonly SUBMIT_ADDITIONAL_PAYMENT_DETAILS = (deliveryPoint: number, orderCode: number) =>
      this.makeUrl(`orders/payments/details/${deliveryPoint}/${orderCode}/v2`);

  public readonly DELETE_STORED_PAYMENT_METHOD = (storedPaymentMethodId: string, deliveryPointCode: number) =>
      this.makeUrl(`storedPaymentMethods/${storedPaymentMethodId}/${deliveryPointCode}`);

  public readonly PAYMENT_METHODS = (deliveryPointCode: number) =>
      this.makeUrl(`paymentMethods/${deliveryPointCode}`);
}
